// assembleKey.js

// ---- Stable stringify (always returns a string) ----
export function stableStringify(obj) {
  if (obj === undefined) return '"__UNDEFINED__"';
  if (obj === null) return 'null';
  if (Array.isArray(obj)) {
    return `[${obj.map(stableStringify).join(",")}]`;
  }
  if (typeof obj === "object") {
    const keys = Object.keys(obj).sort();
    return `{${keys.map(k => `"${k}":${stableStringify(obj[k])}`).join(",")}}`;
  }
  return JSON.stringify(obj);
}

// ---- Tiny hash (djb2-xor) ----
export function hashString(s) {
  const str = String(s); // safety
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(16);
}

// ---- Normalizers (deterministic order & minimal fields) ----
export function normalizeAllocations(allocs = []) {
  const n = (v) => (v == null ? null : Number(v));

  const toSortedNumArray = (raw) => {
    if (Array.isArray(raw)) {
      return raw.map(Number).filter(x => !Number.isNaN(x)).sort((a, b) => a - b);
    }
    if (raw == null) return [];
    const one = Number(raw);
    return Number.isNaN(one) ? [] : [one];
  };

  const norm = (allocs || []).map(a => {
    const rawScenario = a.scenarioId ?? a.scenario_id;
    const rawTargetScenario = a.targetScenarioId ?? a.target_scenario_id;

    return {
      // include IDs that matter for correctness + cache invalidation
      allocationId: n(a.allocationId ?? a.allocation_id),
      businessCaseId: n(a.businessCaseId ?? a.business_case_id),
      organizationId: n(a.organizationId ?? a.organization_id),

      entityId: n(a.entityId ?? a.entity_id) ?? 0,
      accountId: n(a.accountId ?? a.account_id),

      targetEntityId: n(a.targetEntityId ?? a.target_entity_id),
      targetAccountId: n(a.targetAccountId ?? a.target_account_id),

      // always arrays, sorted
      scenarioId: toSortedNumArray(rawScenario),
      targetScenarioId: toSortedNumArray(rawTargetScenario),

      // keep behavior fields you already use
      mode: a.mode ?? null,
      ratio: a.ratio == null ? null : Number(a.ratio),
    };
  })
  .filter(x => x.businessCaseId); // avoid null/0 pollution

  // Precompute stable keys used for sorting
  const keyOf = (x) => JSON.stringify([
    x.scenarioId,
    x.targetScenarioId,
    x.mode,
    x.ratio,
  ]);

  return norm.sort((a, b) =>
    (a.businessCaseId ?? 0) - (b.businessCaseId ?? 0) ||
    (a.entityId ?? 0) - (b.entityId ?? 0) ||
    (a.accountId ?? 0) - (b.accountId ?? 0) ||
    (a.targetEntityId ?? 0) - (b.targetEntityId ?? 0) ||
    (a.targetAccountId ?? 0) - (b.targetAccountId ?? 0) ||
    (a.allocationId ?? 0) - (b.allocationId ?? 0) ||
    keyOf(a).localeCompare(keyOf(b))
  );
}

export function normalizePeriods(periods = []) {
  const coerce = (v, d = 0) => (v == null ? d : Number(v));
  const list = periods.map(p => ({
    id: p.id ?? p.period_id ?? null,
    period: String(p.period ?? ""),     // e.g. "2024-01"
    year: coerce(p.year),
    index: coerce(p.index),
  }));
  return list.sort(
    (a, b) => a.index - b.index || a.year - b.year || a.period.localeCompare(b.period)
  );
}

// ---- Hashers (hash *normalized* data) ----
export function computeAllocationsHash(allocations) {
  return hashString(stableStringify(normalizeAllocations(allocations)));
}

export function computePeriodsHash(periods) {
  return hashString(stableStringify(normalizePeriods(periods)));
}

// meta = { bcId, expandedMask, pushback?, allocationsHash, periodsHash, structureVersion? }
export function makeAssemblyKey(meta) {
  return [
    meta.bcId,
    meta.expandedMask,                          // keep this name consistent at call sites
    meta.structureVersion ?? "v0",
    meta.allocationsHash,
    meta.periodsHash,
    meta.scenarioStamp ?? "",
    stableStringify(meta.pushback ?? null),
  ].join("|");
}

export function getAssemblyBucketKey({
  businessCaseId,
  allocationsHash,
  expandedPeriods,
  scenarioStamp,
  structureVersion,
  periodsHash,
}) {
  return [
    `assembly:${businessCaseId}`,
    allocationsHash ? `alloc:${allocationsHash}` : null,
    expandedPeriods ? `exp:${expandedPeriods}` : null,
    scenarioStamp ? `sc:${scenarioStamp}` : null,
    periodsHash ? `ph:${periodsHash}` : null,
    structureVersion ? `sv:${structureVersion}` : null,
  ]
    .filter(Boolean)
    .join(":");
}

